import type { EventTypeCode } from "../types/events";

export type EventBadgeVariant = "blue" | "purple" | "red" | "amber" | "green";

export const EVENT_BADGE_VARIANT_CLASSES: Record<
	EventBadgeVariant,
	{ bg: string; border: string; dot: string; text: string }
> = {
	blue: {
		bg: "bg-blue-50",
		border: "border-blue-200",
		dot: "bg-blue-500",
		text: "text-blue-900",
	},
	purple: {
		bg: "bg-purple-50",
		border: "border-purple-200",
		dot: "bg-purple-500",
		text: "text-purple-900",
	},
	red: {
		bg: "bg-red-50",
		border: "border-red-200",
		dot: "bg-red-500",
		text: "text-red-900",
	},
	amber: {
		bg: "bg-amber-50",
		border: "border-amber-200",
		dot: "bg-amber-500",
		text: "text-amber-950",
	},
	green: {
		bg: "bg-green-50",
		border: "border-green-200",
		dot: "bg-green-500",
		text: "text-green-900",
	},
};

export type EventFieldFormat =
	| "seconds"
	| "kmh"
	| "km"
	| "limit_kmh"
	| "limit_g"
	| "number"
	| "text"
	| "boolean_pt";

export type EventTechnicalFieldDef = {
	id: string;
	label: string;
	description: string;
	sourceKeys: readonly string[];
	format: EventFieldFormat;
};

export type EventTypeUiEntry = {
	listTitle: string;
	badgeVariant: EventBadgeVariant;
	technicalFields: readonly EventTechnicalFieldDef[];
	emptyTechnicalFallback?: {
		label: string;
		value: string;
		description: string;
	};
};

export const TRACKING_EVENTS_ALLOWED_TYPES = [
	1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
] as const satisfies readonly EventTypeCode[];

export const TRACKING_EVENT_TYPE_SET = new Set<EventTypeCode>(
	TRACKING_EVENTS_ALLOWED_TYPES
);

export const TRACKING_EVENTS_DEFAULT_QUERY_TYPES: EventTypeCode[] = [
	...TRACKING_EVENTS_ALLOWED_TYPES,
];

export const EVENT_TYPE_UI = {
	1: {
		listTitle: "Motor ocioso",
		badgeVariant: "blue",
		technicalFields: [
			{
				id: "seconds",
				label: "Tempo ocioso",
				description: "Tempo parado com o motor ligado",
				sourceKeys: ["seconds"],
				format: "seconds",
			},
			// {
			// 	id: "limit",
			// 	label: "Limite",
			// 	description:
			// 		"",
			// 	sourceKeys: ["limit"],
			// 	format: "seconds",
			// },
		],
	},
	2: {
		listTitle: "Excesso de velocidade",
		badgeVariant: "amber",
		technicalFields: [
			{
				id: "speed",
				label: "Velocidade",
				description: "Velocidade medida na via",
				sourceKeys: ["speed", "max_speed"],
				format: "kmh",
			},
			// {
			// 	id: "limit",
			// 	label: "Limite",
			// 	description:
			// 		"",
			// 	sourceKeys: ["limit"],
			// 	format: "limit_kmh",
			// },
		],
	},
	3: {
		listTitle: "Aceleração brusca",
		badgeVariant: "amber",
		technicalFields: [
			// {
			// 	id: "speed_ini",
			// 	label: "Velocidade inicial",
			// 	description:
			// 		"Velocidade registrada no início do intervalo de aceleração brusca",
			// 	sourceKeys: ["speed_ini", "speedIni"],
			// 	format: "kmh",
			// },
			{
				id: "speed_final",
				label: "Aceleração",
				description: "Aumento de velocidade registrado",
				sourceKeys: ["speed_final", "speedFinal"],
				format: "kmh",
			},
			// {
			// 	id: "limit",
			// 	label: "Limite",
			// 	description: "Limiar usado na detecção da aceleração brusca",
			// 	sourceKeys: ["limit"],
			// 	format: "limit_kmh",
			// },
			// {
			// 	id: "acceleration",
			// 	label: "Aceleração",
			// 	description: "Aumento de velocidade registrado",
			// 	sourceKeys: ["acceleration"],
			// 	format: "text",
			// },
		],
	},
	4: {
		listTitle: "Curva brusca",
		badgeVariant: "amber",
		technicalFields: [
			// {
			// 	id: "course_ini",
			// 	label: "Curva inicial",
			// 	description: "Direção inicial em graus (0° a 360°)",
			// 	sourceKeys: ["course_ini", "courseIni"],
			// 	format: "number",
			// },
			{
				id: "g_force",
				label: "Força G",
				description: "Força exercida durante a curva",
				sourceKeys: ["g_force", "gForce"],
				format: "number",
			},
			// {
			// 	id: "speed",
			// 	label: "Velocidade",
			// 	description: "Velocidade medida na via durante a curva",
			// 	sourceKeys: ["speed", "max_speed"],
			// 	format: "kmh",
			// },
			// {
			// 	id: "limit",
			// 	label: "Força G",
			// 	description: "Força exercida durante a curva",
			// 	sourceKeys: ["limit"],
			// 	format: "limit_g",
			// },
		],
	},
	5: {
		listTitle: "Frenagem brusca",
		badgeVariant: "amber",
		technicalFields: [
			// {
			// 	id: "speed_ini",
			// 	label: "Velocidade inicial",
			// 	description:
			// 		"Velocidade registrada no início do intervalo de frenagem brusca",
			// 	sourceKeys: ["speed_ini", "speedIni"],
			// 	format: "kmh",
			// },
			{
				id: "speed_final",
				label: "Desaceleração",
				description: "Redução de velocidade registrada",
				sourceKeys: ["speed_final", "speedFinal"],
				format: "kmh",
			},
			// {
			// 	id: "limit",
			// 	label: "Limite",
			// 	description: "Limiar usado na detecção da frenagem brusca",
			// 	sourceKeys: ["limit"],
			// 	format: "limit_kmh",
			// },
		],
	},
	6: {
		listTitle: "Entrada na cerca",
		badgeVariant: "red",
		technicalFields: [
			{
				id: "fence",
				label: "Cerca",
				description: "Nome da cerca adentrada",
				sourceKeys: ["name", "Name", "fence", "Fence", "id", "Id"],
				format: "text",
			},
		],
	},
	7: {
		listTitle: "Saída da cerca",
		badgeVariant: "red",
		technicalFields: [
			{
				id: "fence",
				label: "Cerca",
				description: "Nome da cerca abandonada",
				sourceKeys: ["name", "Name", "fence", "Fence", "id", "Id"],
				format: "text",
			},
		],
	},
	8: {
		listTitle: "Bloqueio",
		badgeVariant: "red",
		technicalFields: [],
	},
	9: {
		listTitle: "Desconexão",
		badgeVariant: "amber",
		technicalFields: [
			{
				id: "signal",
				label: "Desconexão",
				description: "Status da conexão do veículo",
				sourceKeys: [
					"signal",
					"Signal",
					"connectionStatus",
					"ConnectionStatus",
				],
				format: "text",
			},
		],
		emptyTechnicalFallback: {
			label: "Desconexão",
			value: "Alimentação do hardware cortada",
			description: "Status da conexão do veículo",
		},
	},
	10: {
		listTitle: "Fora do expediente",
		badgeVariant: "red",
		technicalFields: [
			{
				id: "time",
				label: "Horário do expediente",
				description:
					"Horário previsto de expediente conforme configuração do veículo",
				sourceKeys: ["time", "Time"],
				format: "text",
			},
		],
	},
	11: {
		listTitle: "Excesso de velocidade com chuva",
		badgeVariant: "amber",
		technicalFields: [
			{
				id: "speed",
				label: "Velocidade",
				description: "Velocidade medida na via",
				sourceKeys: ["speed", "max_speed"],
				format: "kmh",
			},
			{
				id: "limit",
				label: "Limite",
				description:
					"Limite de velocidade da via no momento do excesso de velocidade com chuva",
				sourceKeys: ["limit"],
				format: "limit_kmh",
			},
		],
	},
	12: {
		listTitle: "Bateria baixa",
		badgeVariant: "purple",
		technicalFields: [
			{
				id: "voltage",
				label: "Tensão da bateria",
				description: "Tensão registrada",
				sourceKeys: ["voltage", "Voltage"],
				format: "text",
			},
		],
	},
	13: {
		listTitle: "Preventiva",
		badgeVariant: "green",
		technicalFields: [
			{
				id: "current_km",
				label: "Quilometragem",
				description: "Distância em relação a próxima manutenção",
				sourceKeys: ["current_km", "currentKm"],
				format: "km",
			},
			// {
			// 	id: "goal_km",
			// 	label: "Meta de quilômetros",
			// 	description: "Quilometragem alvo para a manutenção preventiva",
			// 	sourceKeys: ["goal_km", "goalKm"],
			// 	format: "km",
			// },
		],
	},
} as const satisfies Record<EventTypeCode, EventTypeUiEntry>;

/** Operational block copy (not tied to eventType). */
export const TRACKING_EVENT_OPERATIONAL_COPY = {
	workSchedule: {
		label: "Expediente do veículo",
		description: "Horário ideal de uso do veículo",
	},
	distanceKm: {
		label: "Distância percorrida",
		description: "Distância percorrida durante o evento",
	},
	fuelLiters: {
		label: "Consumo de combustível",
		description: "Quantidade de combustível consumida no evento",
	},
	fuelType: {
		label: "Tipo de combustível",
		description: "Combustível padrão utilizado no veículo",
	},
} as const;

export const TRACKING_EVENT_TRIPS_ROW_COPY = {
	label: "Viagens realizadas",
	description: "Quantidade de viagens registradas durante o evento",
} as const;

export const FUEL_TYPE_LABELS_PT: Record<number, string> = {
	1: "Gasolina",
	2: "Etanol",
	3: "Flex",
	4: "Diesel",
	5: "GNV",
};
